// © 2026 김용현
/**
 * flowGeometry — 화면 픽셀 좌표계의 순수 기하.
 * 곡선은 2차 베지어 하나로 충분하다(flowmap.blue 도 완만한 호 하나).
 */

/**
 * p0 → p1 굽은 곡선의 점 목록.
 * 진행 방향의 오른쪽으로 굽혀 A→B 와 B→A 가 서로 반대편으로 갈라진다.
 * @param {[number,number]} p0
 * @param {[number,number]} p1
 * @param {{ bend?: number, samples?: number }} [o]  bend = 제어점 오프셋 비율(현 길이 대비).
 *   실제 곡선이 드러내는 정점 처짐(sag)은 이 값의 절반이다 — 예: bend=0.2, len=100 → sag ≈ 10.
 */
export function curvePoints(p0, p1, { bend = 0.2, samples = 24 } = {}) {
  // samples 는 정수 ≥1 로 고정 — 0/음수는 NaN(0으로 나눔)을, 소수는 p1 못 미쳐 끝나는 것을 막는다
  const n = Math.max(1, Math.round(samples));
  const dx = p1[0] - p0[0];
  const dy = p1[1] - p0[1];
  const len = Math.hypot(dx, dy);
  if (len === 0) return [p0.slice(), p1.slice()];
  // 화면 좌표(y 아래)에서 (dx,dy)의 오른쪽 법선은 (-dy, dx)
  const cx = (p0[0] + p1[0]) / 2 + (-dy / len) * bend * len;
  const cy = (p0[1] + p1[1]) / 2 + (dx / len) * bend * len;
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    pts.push([
      u * u * p0[0] + 2 * u * t * cx + t * t * p1[0],
      u * u * p0[1] + 2 * u * t * cy + t * t * p1[1]
    ]);
  }
  return pts;
}

/**
 * p0 → p1 선분을 진행 방향의 오른쪽으로 offset 만큼 평행 이동한 두 끝점.
 * flowmap.blue 처럼 직선으로 그릴 때 A→B 와 B→A 가 서로 다른 쪽으로 비켜서 나란히 붙는다.
 * 같은 점 둘이면 그대로 돌려준다.
 */
export function offsetSegment(p0, p1, offset) {
  const dx = p1[0] - p0[0];
  const dy = p1[1] - p0[1];
  const len = Math.hypot(dx, dy);
  if (len === 0) return [p0.slice(), p1.slice()];
  const nx = -dy / len; // 화면 좌표(y 아래)에서 오른쪽 법선
  const ny = dx / len;
  return [
    [p0[0] + nx * offset, p0[1] + ny * offset],
    [p1[0] + nx * offset, p1[1] + ny * offset]
  ];
}

/**
 * 폴리라인을 따라 폭이 w0(출발) → w1(도착) 로 변하는 닫힌 다각형.
 * 바깥쪽(진행 방향의 오른쪽) 변을 앞으로, 안쪽(왼쪽) 변을 뒤로 이어 fill 한 번으로 그린다.
 */
export function taperOutline(points, w0, w1) {
  const n = points.length;
  if (n < 2) return [];
  const outer = [];
  const inner = [];
  for (let i = 0; i < n; i++) {
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(n - 1, i + 1)];
    let nx = -(next[1] - prev[1]);
    let ny = next[0] - prev[0];
    const l = Math.hypot(nx, ny) || 1;
    nx /= l; ny /= l;
    const half = (w0 + (w1 - w0) * (i / (n - 1))) / 2;
    outer.push([points[i][0] + nx * half, points[i][1] + ny * half]);
    inner.push([points[i][0] - nx * half, points[i][1] - ny * half]);
  }
  return outer.concat(inner.reverse());
}

/** 폴리라인의 누적 길이 배열 (cum[0] = 0, cum[n-1] = 전체 길이) */
export function cumulativeLengths(points) {
  const cum = [0];
  for (let i = 1; i < points.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]));
  }
  return cum;
}

/**
 * 폴리라인을 따라 거리 d 만큼 간 지점과 그곳의 진행 방향 단위 벡터.
 * d 가 범위를 벗어나면 양 끝으로 고정한다. 화살표를 선 위에 늘어놓을 때 쓴다.
 * @returns {{ x, y, tx, ty }}
 */
export function pointAlong(points, cum, d) {
  const n = points.length;
  if (n === 1) return { x: points[0][0], y: points[0][1], tx: 1, ty: 0 };
  const total = cum[n - 1];
  const dd = Math.min(total, Math.max(0, d));
  let i = 1;
  while (i < n - 1 && cum[i] < dd) i++;
  const segLen = cum[i] - cum[i - 1] || 1;
  const t = (dd - cum[i - 1]) / segLen;
  const [ax, ay] = points[i - 1];
  const [bx, by] = points[i];
  return { x: ax + (bx - ax) * t, y: ay + (by - ay) * t, tx: (bx - ax) / segLen, ty: (by - ay) / segLen };
}

/** 점에서 폴리라인까지의 최단 거리 (테스트·검증용) */
export function distanceToPolyline(pt, points) {
  if (points.length === 1) return Math.hypot(pt[0] - points[0][0], pt[1] - points[0][1]);
  let best = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    const [ax, ay] = points[i];
    const [bx, by] = points[i + 1];
    const vx = bx - ax, vy = by - ay;
    const len2 = vx * vx + vy * vy || 1;
    const t = Math.min(1, Math.max(0, ((pt[0] - ax) * vx + (pt[1] - ay) * vy) / len2));
    best = Math.min(best, Math.hypot(pt[0] - (ax + vx * t), pt[1] - (ay + vy * t)));
  }
  return best;
}
